import { CalendarPlottedEvent } from '../../services/enterpriseCalendarsApi';
import { plottedEventOccursOnDay, formatEventTimeLabel } from './calendarRecurrenceUtils';
import { getReadableTextColor, isSameDay, WEEKDAY_LABELS } from './calendarUtils';

type CalendarMonthViewProps = {
  month: Date;
  events: CalendarPlottedEvent[];
  onDayClick?: (day: Date) => void;
  onEventClick?: (event: CalendarPlottedEvent) => void;
};

export function CalendarMonthView({ month, events, onDayClick, onEventClick }: CalendarMonthViewProps) {
  const today = new Date();
  const days = getMonthDays(month);

  return (
    <div className="flex h-full min-h-0 flex-col rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="grid grid-cols-7 border-b border-slate-200 bg-slate-50 text-center text-xs font-semibold uppercase tracking-wide text-slate-500">
        {WEEKDAY_LABELS.map((label) => (
          <div key={label} className="px-2 py-3">
            {label}
          </div>
        ))}
      </div>

      <div className="grid min-h-0 flex-1 auto-rows-fr grid-cols-7">
        {days.map((day) => {
          const inMonth = day.getMonth() === month.getMonth();
          const isToday = isSameDay(day, today);
          const dayEvents = events.filter((event) => plottedEventOccursOnDay(event, day));

          return (
            <button
              key={day.toISOString()}
              type="button"
              className={`min-h-[110px] border-b border-r border-slate-100 p-2 text-left align-top transition-colors hover:bg-slate-50 ${
                inMonth ? 'bg-white' : 'bg-slate-50/80 text-slate-400'
              }`}
              onClick={() => onDayClick?.(day)}
            >
              <div className="mb-1 flex justify-end">
                <span
                  className={`inline-flex h-7 w-7 items-center justify-center rounded-full text-sm ${
                    isToday ? 'bg-primary-600 font-semibold text-white' : ''
                  }`}
                >
                  {day.getDate()}
                </span>
              </div>

              <div className="space-y-1">
                {dayEvents.slice(0, 3).map((event) => (
                  <button
                    key={`${event.calendarId}-${event.id}-${day.toISOString()}`}
                    type="button"
                    className="block w-full truncate rounded px-1.5 py-0.5 text-left text-[11px] font-medium"
                    style={{ backgroundColor: event.color, color: getReadableTextColor(event.color) }}
                    title={`${event.name}${formatEventTimeLabel(event) ? ` (${formatEventTimeLabel(event)})` : ''} (${event.calendarName})`}
                    onClick={(clickEvent) => {
                      clickEvent.stopPropagation();
                      onEventClick?.(event);
                    }}
                  >
                    {event.name}
                  </button>
                ))}
                {dayEvents.length > 3 ? (
                  <p className="px-1 text-[11px] text-slate-500">+{dayEvents.length - 3} mais</p>
                ) : null}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function getMonthDays(month: Date): Date[] {
  const firstDay = new Date(month.getFullYear(), month.getMonth(), 1);
  const start = new Date(firstDay);
  start.setDate(firstDay.getDate() - firstDay.getDay());

  const days: Date[] = [];
  for (let index = 0; index < 42; index += 1) {
    const day = new Date(start);
    day.setDate(start.getDate() + index);
    days.push(day);
  }

  return days;
}
